import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MarkdownContent from "@/components/MarkdownContent";
import HideThreadModal from "@/components/HideThreadModal";
import {
  Flag, EyeOff, Eye, ExternalLink, MessageSquare, ShieldCheck, ArrowRight,
  History, Clock, User,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function StaffForos() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [tab, setTab] = useState("reports");

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Moderación
        </p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Foros de la comunidad</h1>
        <p className="mt-2 text-gray-400">
          Revisa reportes y monitorea la actividad de todos los foros.
        </p>

        <div className="mt-8 border-b border-gray-800 flex gap-1" data-testid="foros-tabs">
          <TabBtn active={tab === "reports"} onClick={() => setTab("reports")} testid="tab-reports">
            Reportes pendientes
          </TabBtn>
          <TabBtn active={tab === "forums"} onClick={() => setTab("forums")} testid="tab-forums">
            Todos los foros
          </TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
            Historial de moderación
          </TabBtn>
        </div>

        <div className="mt-6">
          {tab === "reports" && <ReportsTab />}
          {tab === "forums" && <ForumsTab />}
          {tab === "history" && <HistoryTab />}
        </div>
      </div>
    </StaffLayout>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
        active ? "border-[#3B82F6] text-white" : "border-transparent text-gray-400 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function ReportsTab() {
  const [data, setData] = useState({ reported_threads: [], reported_posts: [] });
  const [loading, setLoading] = useState(true);
  const [hiding, setHiding] = useState(null); // {target_type, target_id, currently_hidden}

  const load = () => {
    setLoading(true);
    api.get("/forums/moderation/reports")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const clearThreadReport = async (id) => {
    try {
      await api.patch(`/forums/threads/${id}/hide`, { hidden: false });
      toast.success("Reporte descartado · tema visible");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clearPostReport = async (id) => {
    try {
      await api.patch(`/forums/posts/${id}/hide`, { hidden: false });
      toast.success("Reporte descartado · respuesta visible");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const total = (data.reported_threads?.length || 0) + (data.reported_posts?.length || 0);

  if (total === 0) {
    return (
      <div className="p-10 border border-dashed border-gray-800 rounded-xl text-center" data-testid="reports-empty">
        <Flag className="w-10 h-10 mx-auto text-emerald-500" />
        <p className="mt-3 text-white font-semibold">Sin reportes pendientes</p>
        <p className="mt-1 text-sm text-gray-400">Toda la comunidad limpia por ahora.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-list">
      {data.reported_threads?.length > 0 && (
        <section>
          <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-3">
            Temas reportados · {data.reported_threads.length}
          </p>
          <div className="space-y-3">
            {data.reported_threads.map((t) => (
              <ReportCard
                key={`t-${t.id}`}
                kind="thread"
                item={t}
                onHide={() => setHiding({ target_type: "thread", target_id: t.id, currently_hidden: false })}
                onClear={() => clearThreadReport(t.id)}
              />
            ))}
          </div>
        </section>
      )}

      {data.reported_posts?.length > 0 && (
        <section>
          <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-3">
            Respuestas reportadas · {data.reported_posts.length}
          </p>
          <div className="space-y-3">
            {data.reported_posts.map((p) => (
              <ReportCard
                key={`p-${p.id}`}
                kind="post"
                item={p}
                onHide={() => setHiding({ target_type: "post", target_id: p.id, currently_hidden: false })}
                onClear={() => clearPostReport(p.id)}
              />
            ))}
          </div>
        </section>
      )}

      {hiding && (
        <HideThreadModal
          targetType={hiding.target_type}
          targetId={hiding.target_id}
          currentlyHidden={hiding.currently_hidden}
          onClose={() => setHiding(null)}
          onDone={() => { setHiding(null); load(); }}
        />
      )}
    </div>
  );
}

function ReportCard({ kind, item, onHide, onClear }) {
  const isThread = kind === "thread";
  return (
    <div
      className="p-5 bg-[#111827] border border-amber-500/40 rounded-xl"
      data-testid={`report-card-${kind}-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 uppercase tracking-widest font-semibold">
              <Flag className="w-3 h-3 inline mr-1" />
              {isThread ? "Tema reportado" : "Respuesta reportada"}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded border border-gray-700 text-gray-300 uppercase tracking-widest font-semibold">
              {item.author_role || "usuario"}
            </span>
            <span className="text-[11px] text-gray-500 font-mono">
              reportado {fmtDate(item.reported_at)}
            </span>
          </div>
          {isThread && (
            <h3 className="text-base font-semibold text-white mb-1">{item.title}</h3>
          )}
          {!isThread && item.thread_title && (
            <p className="text-[11px] text-gray-500 mb-1">
              En tema: <span className="text-gray-300">{item.thread_title}</span>
            </p>
          )}
          <div className="text-sm text-gray-300 max-h-32 overflow-hidden">
            <MarkdownContent>{item.body_md}</MarkdownContent>
          </div>
          {item.report_reason && (
            <p className="mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
              <strong>Motivo:</strong> {item.report_reason}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onHide}
          data-testid={`report-hide-${kind}-${item.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
        >
          <EyeOff className="w-3 h-3" /> Ocultar
        </button>
        <button
          type="button"
          onClick={onClear}
          data-testid={`report-clear-${kind}-${item.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
        >
          <Eye className="w-3 h-3" /> Dejar visible
        </button>
        <Link
          to={isThread ? `/app/staff/foros/thread/${item.id}` : `/app/staff/foros/thread/${item.thread_id}`}
          data-testid={`report-view-${kind}-${item.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-white/5 border border-gray-700 text-gray-300 hover:bg-white/10"
        >
          <ExternalLink className="w-3 h-3" /> Ver contexto
        </Link>
      </div>
    </div>
  );
}

function ForumsTab() {
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/forums/moderation/forums")
      .then((r) => setForums(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="h-64 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />;
  }
  if (forums.length === 0) {
    return <p className="text-sm text-gray-500 italic">Sin foros aún.</p>;
  }
  return (
    <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="forums-table">
      <table className="w-full text-sm">
        <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
          <tr>
            <th className="text-left px-4 py-3">Programa</th>
            <th className="text-left px-4 py-3">Código</th>
            <th className="text-right px-4 py-3">Temas</th>
            <th className="text-right px-4 py-3">Respuestas</th>
            <th className="text-left px-4 py-3">Última actividad</th>
            <th className="text-right px-4 py-3">Acción</th>
          </tr>
        </thead>
        <tbody>
          {forums.map((f) => (
            <tr key={f.id} className="border-t border-gray-800" data-testid={`forum-row-${f.id}`}>
              <td className="px-4 py-3 text-white font-medium">{f.program_title}</td>
              <td className="px-4 py-3 text-gray-400 font-mono text-xs">{f.program_code}</td>
              <td className="px-4 py-3 text-right font-mono text-white">{f.threads_count}</td>
              <td className="px-4 py-3 text-right font-mono text-gray-300">{f.posts_count}</td>
              <td className="px-4 py-3 text-gray-500 font-mono text-xs">{fmtDate(f.updated_at)}</td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={`/catalogo/${f.program_slug}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-gray-200 hover:bg-white/5 text-xs font-semibold"
                >
                  Ver programa <ArrowRight className="w-3 h-3" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function HistoryTab() {
  const [items, setItems] = useState([]);
  const [reasonFilter, setReasonFilter] = useState("");
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "50" });
    if (reasonFilter) params.set("reason", reasonFilter);
    api.get(`/forums/moderation/actions?${params.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    api.get("/forums/moderation/reasons").then((r) => {
      setReasons(r.data.items || []);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reasonFilter]);

  const reasonLabel = (code) => {
    const r = reasons.find((rr) => rr.code === code);
    return r ? r.label : (code || "—");
  };

  return (
    <div data-testid="history-tab">
      <div className="mb-4 flex items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
            Filtrar por motivo
          </label>
          <select
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            data-testid="history-reason-filter"
            className="mt-1 bg-[#111827] border border-gray-800 rounded-md px-3 py-1.5 text-xs text-white"
          >
            <option value="">Todos los motivos</option>
            {reasons.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-gray-500 font-mono mb-1">{items.length} acciones</p>
      </div>

      {loading ? (
        <div className="h-64 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
      ) : items.length === 0 ? (
        <div className="p-10 border border-dashed border-gray-800 rounded-xl text-center">
          <History className="w-10 h-10 mx-auto text-gray-500" />
          <p className="mt-3 text-white font-semibold">Sin acciones registradas</p>
          <p className="mt-1 text-sm text-gray-400">Aquí aparecerán ocultar/restaurar de threads y posts.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="history-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-left px-4 py-3">Moderador</th>
                <th className="text-left px-4 py-3">Acción</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Motivo</th>
                <th className="text-left px-4 py-3">Nota</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-gray-800 hover:bg-white/5"
                    data-testid={`history-row-${a.id}`}>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {fmtDate(a.created_at)}
                  </td>
                  <td className="px-4 py-3 text-xs text-white">
                    <User className="w-3 h-3 inline mr-1" />
                    {a.moderator_name || a.moderator_id.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-widest font-semibold ${
                      a.action === "hide"
                        ? "bg-red-500/15 border-red-500/40 text-red-300"
                        : "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    }`}>
                      {a.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">
                    {a.target_type}/{a.target_id.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3 text-xs text-brand-orange-500">
                    {a.reason ? reasonLabel(a.reason) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-300 max-w-md truncate">
                    {a.note || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
